import React, { useState, useEffect, useMemo, useCallback } from "react";
import { fetchPlanOptionsAndPlans, fetchSubOptions } from "../../medPracapi";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";

function PlanOptionSelector({
  schemeCode,
  value = "",
  onChangeId,
  onPlanOptionSelect,
  disabled = false,
}) {
  const [planOptions, setPlanOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!schemeCode) {
      setPlanOptions([]);
      // clear selection when scheme is cleared
      onChangeId?.("");
      return;
    }

    const loadPlanOptions = async () => {
      setLoading(true);
      try {
        const data = await fetchPlanOptionsAndPlans(schemeCode);
        const optionsWithIds = (data || []).map((item) => ({
          ...item,
          id: `${item.option.code}-${item.plan.code}`,
        }));
        setPlanOptions(optionsWithIds);
        console.log("Options", optionsWithIds)
      } catch (error) {
        console.error("Error loading plan options:", error);
        setPlanOptions([]);
      } finally {
        setLoading(false);
      }
    };

    loadPlanOptions();
    // reset selection when scheme changes
  }, [schemeCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedPlanOptions = useMemo(() => {
    return [...planOptions].sort((a, b) =>
      (a.option?.option || "").localeCompare(b.option?.option || "")
    );
  }, [planOptions]);

  // Add this useEffect in PlanOptionSelector
useEffect(() => {
  // If we have a value but it's not in our current options, trigger the selection callback
  if (value && planOptions.length > 0) {
    const selectedItem = planOptions.find((item) => item.id === value);
    if (selectedItem && onPlanOptionSelect) {
      // Trigger the callback to populate parent state
      fetchSubOptions(selectedItem.option.code).then((subOptions) => {
        const firstSubOption = subOptions?.length > 0 ? subOptions[0]?.planSubOption : null;
        const firstDataTransmission = firstSubOption?.dataTransmissions?.[0];

        onPlanOptionSelect({
          patientMedicalAidOption: selectedItem.option.option,
          patientMedicalAidOptionCode: selectedItem.option.code,
          patientMedicalAidPlan: selectedItem.plan.plan,
          patientMedicalAidPlanCode: selectedItem.plan.code,
          patientMediKreditPlanNumber03: firstDataTransmission?.mk03Id || "",
          patientMediKreditPlanNumber04: firstDataTransmission?.mk04Id || "",
          patientMediKreditPlanNumber06: firstDataTransmission?.mk06Id || "",
        });
      });
    }
  }
}, [planOptions, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlanOptionSelect = useCallback(
    async (e) => {
      const selectedId = e.target.value || "";
      onChangeId?.(selectedId);

      if (!selectedId) return;

      const selectedItem = planOptions.find((item) => item.id === selectedId);
      if (!selectedItem) return;

      try {
        const subOptions = await fetchSubOptions(selectedItem.option.code);
        const firstSubOption =
          subOptions?.length > 0 ? subOptions[0]?.planSubOption : null;
        const firstDataTransmission = firstSubOption?.dataTransmissions?.[0];

        onPlanOptionSelect?.({
          patientMedicalAidOption: selectedItem.option.option,
          patientMedicalAidOptionCode: selectedItem.option.code,
          patientMedicalAidPlan: selectedItem.plan.plan,
          patientMedicalAidPlanCode: selectedItem.plan.code,
          patientMediKreditPlanNumber03: firstDataTransmission?.mk03Id || "",
          patientMediKreditPlanNumber04: firstDataTransmission?.mk04Id || "",
          patientMediKreditPlanNumber06: firstDataTransmission?.mk06Id || "",
        });
      } catch (error) {
        console.error("❌ Error fetching subOptions:", error);
      }
    },
    [planOptions, onChangeId, onPlanOptionSelect]
  );



  return (
    <Form.Group controlId="formGridPlanOption">
      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <Form.Select
          value={value}
          onChange={handlePlanOptionSelect}
          disabled={disabled || !schemeCode}
        >
          <option value="">Select Plan Option & Plan</option>
          {sortedPlanOptions.length > 0 ? (
            sortedPlanOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.option.option} | {item.plan.plan}
              </option>
            ))
          ) : (
            <option disabled>No options available</option>
          )}
        </Form.Select>
      )}
    </Form.Group>
  );
}

export default PlanOptionSelector;
